import { useCallback, useEffect, useState } from 'react';

/**
 * Per-person navigation preference: whether the sidebar is collapsed to its
 * icon strip (2026-08-27; the pinned-pages half was dropped on 2026-09-08,
 * re-pointed at the workspace rail 2026-08-31).
 *
 * Stored in localStorage, deliberately. These are conveniences for one person
 * on one machine — a collapsed sidebar is not company
 * data, and putting them on the server would mean a migration, an endpoint and
 * a round trip before the nav could paint. Every read is wrapped because
 * storage throws outright in some contexts (private windows, blocked site
 * data), and a nav that cannot render is far worse than a forgotten preference.
 */

const PANEL_KEY = 'demoops.nav.panel.v1';

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function write(key: string, value: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Nothing to do and nothing worth telling the user — the sidebar still works.
  }
}

/**
 * Is the workspace panel collapsed to its icon strip?
 *
 * It starts EXPANDED: a first-time user must be able to see what the app
 * contains, and hiding it behind a disclosure they have to discover is how a
 * reorganisation makes things harder to find rather than easier. Collapsing is
 * a choice, and it is remembered — the same reasoning as the pins above.
 *
 * Stored as the one-element list ['collapsed'] so it shares read()/write() and
 * their storage-throws guards; a bare "true"/"false" string would need its own
 * pair of wrappers for no benefit.
 */
export function useNavPanelCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => read(PANEL_KEY).includes('collapsed'));

  useEffect(() => { write(PANEL_KEY, collapsed ? ['collapsed'] : []); }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((c) => !c), []);

  return { collapsed, toggle };
}
